import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { WebsiteGroupRecord } from '../../../../shared/models/website-group.models';
import { MatTabsModule } from '@angular/material/tabs';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { combineLatest, filter, map, shareReplay, switchMap } from 'rxjs';
import { DiskUsageComponent } from '../disk-usage/disk-usage.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { StatusModalService } from '../../../../core/services/status-modal.service';
import { UsageService } from '../../../../core/services/usage.service';
import { SnackbarComponent } from '../../../../shared/components/snackbar/snackbar.component';
import { BandwidthComponent } from '../bandwidth/bandwidth.component';
import moment from 'moment';
import { AsyncPipe } from '@angular/common';
import { CpuComponent } from '../cpu/cpu.component';
import { Preset } from '../../../../shared/models/usage.models';

@Component({
  selector: 'app-group-usage',
  imports: [MatTabsModule, DiskUsageComponent, BandwidthComponent, AsyncPipe, CpuComponent],
  templateUrl: './group-usage.component.html',
  styleUrl: './group-usage.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GroupUsageComponent implements OnInit {
  private readonly router = inject(Router);

  private readonly route = inject(ActivatedRoute);

  private usageService = inject(UsageService);

  private snackbar = inject(MatSnackBar);

  private statusModalService = inject(StatusModalService);

  readonly tabs = ['disk-usage', 'bandwidth', 'cpu'] as const;

  activeTab = toSignal(
    this.route.queryParamMap.pipe(map((params) => params.get('tab') ?? 'overview')),
    { initialValue: 'details' },
  );

  selectedTabIndex = computed(() =>
    this.tabs.indexOf(this.activeTab() as (typeof this.tabs)[number]),
  );

  readonly groupData: WebsiteGroupRecord = this.route.snapshot.data['group'];

  groupData$ = this.route.data.pipe(map((data) => data['group'] as WebsiteGroupRecord));

  groupId$ = this.route.paramMap.pipe(map((params) => params.get('id')));

  bandwidth$ = this.groupId$.pipe(
    filter(Boolean),
    switchMap((id) =>
      this.usageService.getBandwidthUsageByGroup(+id, moment().utc().startOf('month').unix()),
    ),
    shareReplay(1),
  );

  cpuUsageRange = signal<Preset>('30d');

  cpuUsage$ = combineLatest({
    data: this.groupData$,
    range: toObservable(this.cpuUsageRange),
  }).pipe(
    filter(({ data }) => data.usage !== undefined),
    switchMap(({ data, range }) =>
      this.usageService.getCpuUsage(data.usage!.systemUserId.toString(), range),
    ),
    shareReplay(1),
  );

  ngOnInit(): void {
    if (!this.groupData) this.router.navigate(['usage']);
  }

  onTabChange(index: number): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        tab: this.tabs[index],
      },
    });
  }

  onPeriodChange(period: Preset): void {
    this.cpuUsageRange.set(period);
  }

  syncWebsiteDiskUsage(): void {
    const groupId = this.groupData.id;
    if (groupId)
      this.usageService.syncWebsiteGroupDiskUsageByGroupId(groupId).subscribe({
        next: () => {
          this.snackbar.openFromComponent(SnackbarComponent, {
            data: {
              text: `Usage sync started`,
            },
          });
        },
        error: (error) => {
          this.statusModalService.open({
            state: 'error',
            message: error.error?.error,
          });
        },
      });
  }
}
